import { HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID, REQUEST } from '@angular/core';
import { isPlatformServer } from '@angular/common';

/**
 * During the SSR render pass, relative `/api/...` URLs have no origin. The API
 * is served by the same Express process, so the render must call back into
 * itself — over the loopback interface, NOT over the public URL.
 *
 * Using the incoming request's origin looks right and works locally, where the
 * incoming origin already is localhost. Behind a proxy it is the public
 * address: the server then leaves the container, goes out through Cloudflare
 * and tries to come back in. That call hangs or is refused, the render has no
 * data, and Angular emits an empty `<app-root>` — the page still works in a
 * browser, because hydration fetches it again from the client, so the failure
 * is invisible unless you look at the served HTML.
 *
 * So the incoming origin is trusted only when it is itself local, which keeps
 * `ng serve` working (its real port differs from the production default);
 * anything else falls back to the loopback address and PORT.
 *
 * 127.0.0.1 rather than `localhost`: Node resolves `localhost` to ::1 first,
 * and Express binds 0.0.0.0 — IPv4 only.
 */
export const ssrBaseUrlInterceptor: HttpInterceptorFn = (req, next) => {
  const platformId = inject(PLATFORM_ID);
  if (isPlatformServer(platformId) && req.url.startsWith('/')) {
    return next(req.clone({ url: `${serverOrigin()}${req.url}` }));
  }
  return next(req);
};

function serverOrigin(): string {
  const loopback = `http://127.0.0.1:${process.env['PORT'] || 4000}`;
  const incoming = inject(REQUEST, { optional: true });
  if (!incoming) return loopback;
  try {
    const url = new URL(incoming.url);
    const isLocal =
      url.hostname === 'localhost' || url.hostname === '127.0.0.1' || url.hostname === '::1';
    return isLocal ? url.origin : loopback;
  } catch {
    return loopback;
  }
}
